import { HTMLAttributes, useCallback, useMemo, useState } from 'react';
import { APIError, MfaError } from '@coinbase/cdp-core';
import { useCurrentUser } from '@coinbase/cdp-hooks';
import { SignOutButton } from '@coinbase/cdp-react';
import { useUser } from 'apps/cdp-web-demo/app/(main)/UserContext';
import ControlBtnLink from 'apps/cdp-web-demo/app/ControlBtnLink';
import ArrowUpRight from 'apps/cdp-web-demo/app/icons/arrowUpRight.svg';
import Checkmark from 'apps/cdp-web-demo/app/icons/checkmarkCircle.svg';
import Error from 'apps/cdp-web-demo/app/icons/error.svg';
import Logout from 'apps/cdp-web-demo/app/icons/logout.svg';

import { AddMfaDialog } from '../AddMfaDialog/AddMfaDialog';
import { BalanceCard } from '../BalanceCard/BalanceCard';
import { Card } from '../Card/Card';
import { ExportWalletDialog } from '../ExportWalletDialog/ExportWalletDialog';
import { LinkProfileDialog } from '../LinkProfileDialog/LinkProfileDialog';
import { SendTransaction } from '../SendTransaction/SendTransaction';
import { UserAddress } from '../UserAddress/UserAddress';

import styles from './LoggedInView.module.css';

type Props = {
  onLinkAccountSuccess?: () => void;
  onTransactionSuccess?: () => void;
};

function LoggedInView({
  className = '',
  onLinkAccountSuccess,
  onTransactionSuccess,
  ...props
}: Props & HTMLAttributes<HTMLDivElement>) {
  const {
    accountCreationType,
    userAddress,
    onSignOutSuccess,
    balance,
    isPendingBalance,
    isBalanceInitialized,
    loggedInView,
  } = useUser();
  const { currentUser } = useCurrentUser();
  const evmEoaAddress = currentUser?.evmAccounts?.[0];
  const exportWalletAddress =
    (accountCreationType === 'evm-smart' ? evmEoaAddress : userAddress) ?? '';
  const isSolana = accountCreationType === 'solana';
  const [hasTxResultOrError, setHasTxResultOrError] = useState(false);

  const [transactionHash, setTransactionHash] = useState('');
  const [error, setError] = useState('');

  const txHashExplorerLink = useMemo(() => {
    return {
      url: isSolana
        ? `https://explorer.solana.com/tx/${transactionHash}?cluster=devnet`
        : `https://sepolia.basescan.org/tx/${transactionHash}`,
      label: `View on ${isSolana ? 'Solscan' : 'BaseScan'}`,
    };
  }, [isSolana, transactionHash]);

  const hasBalance = useMemo(() => {
    return !!balance && balance !== 0n;
  }, [balance]);

  const handleTransactionError: (e: APIError | Error) => void = useCallback((e) => {
    // don't show error if user cancelled mfa veriifcation
    if (e instanceof MfaError && e.code === 'CANCELLED') {
      return;
    }
    setTransactionHash('');
    setError(e.message);
    setHasTxResultOrError(true);
  }, []);

  const handleTransactionSuccess: (hash: string) => void = useCallback(
    (hash) => {
      setTransactionHash(hash);
      setError('');
      onTransactionSuccess?.();
      setHasTxResultOrError(true);
    },
    [onTransactionSuccess],
  );

  const handleReset = useCallback(() => {
    setTransactionHash('');
    setError('');
  }, []);

  const handleCloseTxResult = useCallback(() => {
    setHasTxResultOrError(false);
    setTimeout(() => {
      handleReset();
    }, 250);
  }, [handleReset]);

  return (
    <>
      <div
        inert={loggedInView ? true : undefined}
        className={`${styles['logged-in']} ${className}`}
        {...props}
      >
        <div className={styles['heading-row']}>
          <UserAddress accountCreationType={accountCreationType} userAddress={userAddress} />
          <p className={styles['sign-out-button']}>
            <SignOutButton variant="transparentSecondary" onSuccess={onSignOutSuccess}>
              <>
                <Logout />
                <span>Sign out</span>
              </>
            </SignOutButton>
          </p>
        </div>
        <BalanceCard
          isPendingBalance={isPendingBalance && !isBalanceInitialized}
          accountCreationType={accountCreationType}
          balance={balance}
          userAddress={userAddress}
        >
          {hasBalance && (
            <div data-button-container className={styles['button-container']}>
              <SendTransaction
                accountCreationType={accountCreationType}
                userAddress={userAddress}
                onClick={handleCloseTxResult}
                onSuccess={handleTransactionSuccess}
                onError={handleTransactionError}
              />
            </div>
          )}
        </BalanceCard>
        <div
          className={styles['tx-result-container']}
          data-state={hasTxResultOrError ? 'open' : 'closed'}
        >
          {!!transactionHash && (
            <Card
              heading={
                <span className={styles.row}>
                  <span className={`${styles['row-icon']} ${styles.success}`}>
                    <Checkmark aria-hidden="true" role="img" />
                  </span>
                  <span>Transaction sent!</span>
                </span>
              }
              onClose={handleCloseTxResult}
            >
              <div>
                <ControlBtnLink href={txHashExplorerLink.url}>
                  <span>{txHashExplorerLink.label}</span>
                  <ArrowUpRight aria-hidden="true" role="img" className={styles['ext-link-icon']} />
                </ControlBtnLink>
              </div>
            </Card>
          )}
          {!!error && (
            <Card
              className={styles['error-card']}
              heading={
                <span className={styles.row}>
                  <span className={`${styles['row-icon']} ${styles.error}`}>
                    <Error aria-hidden="true" role="img" />
                  </span>
                  <span>Transaction failed</span>
                </span>
              }
              onClose={handleCloseTxResult}
            >
              <p>{error}</p>
            </Card>
          )}
        </div>
      </div>
      <LinkProfileDialog />
      <AddMfaDialog />
      <ExportWalletDialog address={exportWalletAddress} />
    </>
  );
}

export { LoggedInView };
